// SignInPage — C-LOGIN.
// Mirrors review/commerce-app/auth.jsx (CmLogin).
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/pages/AuthPages.tsx `SignInPage`. Every departure
// from that file is marked ADAPTED and explains itself.
//
// ADAPTED (throughout): design-system components are imported by module path
// rather than from the `@miq/design-system` barrel, for the reason AppShell.tsx
// sets out — the barrel pulls in modules importing packages this app does not
// install, five of them version-pinned to specifiers no bundler resolves.
//
// ADAPTED (throughout): DS button handlers are `onPress`, the React Aria event
// the DS Button is built on, which fires for pointer, keyboard and touch alike.
//
// The shape of the screen is the drop's: one form — email, password, and a
// "Continue with SSO" button beneath a separator, all of it visible at once.
// It briefly was not. An earlier reading asked for the EMAIL FIRST and drew one
// door or the other from what the service answered about the address, which
// made the page an account-existence oracle: a page that routes on the address
// has to say which way it is routing. Identifier-first is the pattern for a
// service choosing among MANY identity providers, and this one has a single
// broker that makes the routing decision itself — so the round trip was
// answering a question that was never this app's (Owner ruling, 2026-09-02).
// The screen now looks identical to every visitor, which is what the drop drew.
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from '@miq/design-system/components/ui/button';
import { Checkbox } from '@miq/design-system/components/ui/checkbox';
import { Input } from '@miq/design-system/components/ui/input';
import { Label } from '@miq/design-system/components/ui/label';
import { Separator } from '@miq/design-system/components/ui/separator';

import { AuthShell } from '../components/AuthShell';
import { usePortalSession } from '../auth/context';
import {
  FEDERATED_REFUSALS,
  FEDERATED_START_URL,
  federatedStartFields,
} from '../auth/federation';
import { useDocumentTitle } from '../../lib/documentTitle';

export function SignInPage() {
  useDocumentTitle('Sign in');
  const { signIn } = usePortalSession();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [refusal, setRefusal] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // A federated round trip that came back refused. Read off the URL because
  // the backend is mid-navigation when it decides — the browser is being sent
  // somewhere, not answered — so the reason travels as a redirect parameter.
  // Only tokens this app knows are shown; an unrecognised one says nothing.
  const federatedRefusal = FEDERATED_REFUSALS[searchParams.get('federated') ?? ''];

  // ADAPTED: the handoff's Sign in button navigates. A real sign-in submits,
  // which makes this a form — so Enter works from either field, which is how
  // people sign in — and can be refused, which the handoff has nowhere to say.
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setRefusal('');
    setSubmitting(true);
    try {
      await signIn(email, password, keepSignedIn);
    } catch (failure) {
      // The service's own wording, shown as it was given: it deliberately does
      // not distinguish an unknown address from a wrong password, and softening
      // that here into "check your email" would undo the reason.
      setRefusal(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSubmitting(false);
    }
  };

  const alert = refusal || federatedRefusal;

  return (
    <AuthShell>
      <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Commerce portal — decision packs, fleet, metering, and billing.
      </p>

      {alert && (
        // ADAPTED: the handoff has no refusal state, because a prototype
        // cannot be refused. `role="alert"` so a screen reader hears it.
        <p role="alert" className="mt-4 text-sm text-destructive">
          {alert}
        </p>
      )}

      <form onSubmit={submit} noValidate>
        <div className="mt-5 space-y-1.5">
          <Label htmlFor="portal-email">Work email</Label>
          <Input
            id="portal-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="mt-4 space-y-1.5">
          <Label htmlFor="portal-password">Password</Label>
          <Input
            id="portal-password"
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••••"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox isSelected={keepSignedIn} onChange={setKeepSignedIn} /> Keep me
            signed in
          </label>
          {/* ADAPTED: the handoff's Forgot password? is inert. This service
              already has a working password reset — allauth's, on the
              server-rendered side — so the link goes there rather than to a
              second reset flow the portal would have to grow. */}
          <a
            href="/accounts/password/reset/"
            className="text-sm text-link underline-offset-4 hover:underline"
          >
            Forgot password?
          </a>
        </div>
        <Button type="submit" className="mt-5 w-full" isPending={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <Separator className="flex-1" />
        or
        <Separator className="flex-1" />
      </div>

      {/* ADAPTED: Continue with SSO is the drop's button, live. It is a real
          form POST rather than a fetch or a link, for two reasons: the browser
          has to LEAVE for the identity provider, and beginning a login is a
          credential-shaped act that the backend protects with a CSRF token —
          allauth guards its own provider-login address with an interstitial
          confirmation page for the same reason, and this replaces that second
          click with the token the app already holds.

          The keep-me-signed-in box above governs this door too, which is why
          this form carries that box's value rather than drawing a second one:
          one decision, made once, whichever door the person then presses.

          The address goes with it when there is one and is left out when there
          is not — a hint for the provider, never a condition of pressing the
          button. Deliberately NOT required: somebody whose company federates
          may have typed nothing, and the provider asks them itself. */}
      <form method="post" action={FEDERATED_START_URL}>
        {Object.entries(federatedStartFields(keepSignedIn, email)).map(([name, value]) => (
          <input key={name} type="hidden" name={name} value={value} />
        ))}
        <Button type="submit" variant="outline" className="w-full">
          <Lock className="size-3.5" /> Continue with SSO
        </Button>
        <p className="mt-3 text-xs text-muted-foreground">
          You will finish signing in at your company&apos;s identity provider.
        </p>
      </form>

      <p className="mt-5 text-center text-sm text-muted-foreground">
        {/* The claim journey shipped (/portal/claim), so the sentence that
            deferred it to a later phase was false. The wording matches the
            journey's own last screen (claim/ClaimJourney.tsx), because it is
            the same instruction said in the place somebody reads it first. */}
        New customer from a marketplace? Open the subscription from your
        marketplace console to claim it.
      </p>

      {/*
        The signpost for metricsIQ staff, who sign in HERE — same fields, same
        button, same door — and are told apart by what the service knows about
        them rather than by which address they typed. Where they land is the
        portal's answer to /portal/api/me/ and nothing this screen decides.

        It used to send them to /admin/login/, and that sentence was false in
        both halves: /admin/ is Django's own database console and was never the
        way into these screens, and this door refused a metricsIQ person only
        while acting for a customer was the sole way to be entitled to
        anything. It is not any more — a role is the other way — so the dead
        end that sentence documented (a refusal worded as a password failure,
        and an operations surface answering 404 rather than offering a
        sign-in; met by the Owner on 2026-08-31) has no remaining half.

        Static text for everyone, which is why it leaks nothing: it is equally
        true for every visitor and says nothing about the address in the field
        above. Truer of this screen than of the one before it, which needed a
        list of the operator's own email domains to draw metricsIQ's people an
        SSO button at all — a button drawn for everybody reaches them without
        naming anybody.
      */}
      <p className="mt-2 text-center text-xs text-muted-foreground">
        metricsIQ staff sign in here too, with the same work email.
      </p>
    </AuthShell>
  );
}
